import React, { useState } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import FadeIn from '../components/FadeIn';
import { commissionProcess, artistInfo } from '../data/site';

const ProcessSteps = ({ steps }) => (
  <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-20">
    <FadeIn><h2 className="text-lg font-light text-[#1a1a1a] mb-10">The process</h2></FadeIn>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
      {steps.map((step) => (
        <FadeIn key={step.number} delay={parseInt(step.number, 10) * 100}>
          <div className="relative">
            <span className="text-[40px] font-light text-[#eee] leading-none select-none">{step.number}</span>
            <h3 className="text-[14px] font-medium text-[#1a1a1a] mt-3 mb-3">{step.title}</h3>
            <p className="text-[13px] leading-[1.7] text-[#888]">{step.description}</p>
          </div>
        </FadeIn>
      ))}
    </div>
  </section>
);

const PricingTimeline = ({ pricing, timeline }) => (
  <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-20">
    <FadeIn>
      <div className="border-t border-[#eee] pt-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <h3 className="text-[13px] font-medium text-[#aaa] mb-4">Pricing</h3>
            <p className="text-[14px] leading-[1.75] text-[#555]">{pricing}</p>
          </div>
          <div>
            <h3 className="text-[13px] font-medium text-[#aaa] mb-4">Timeline</h3>
            <p className="text-[14px] leading-[1.75] text-[#555]">{timeline}</p>
          </div>
        </div>
      </div>
    </FadeIn>
  </section>
);

const PastCommissionsGallery = ({ pastWork }) => (
  <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-20">
    <FadeIn><h2 className="text-lg font-light text-[#1a1a1a] mb-8">Past commissions</h2></FadeIn>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {pastWork.map((img) => (
        <FadeIn key={img}>
          <div className="aspect-square overflow-hidden bg-[#f5f5f5]">
            <img src={img} alt="Past commission" className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-[1.2s] ease-out" />
          </div>
        </FadeIn>
      ))}
    </div>
  </section>
);

const inputClass = 'w-full bg-transparent border-b border-[#e5e5e5] focus:border-[#999] text-[#1a1a1a] text-[14px] py-2.5 outline-none transition-colors duration-300 placeholder:text-[#ddd]';

const PersonalInfoFields = ({ formData, handleChange }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
    <div>
      <label className="text-[11px] text-[#bbb] block mb-2">Your name <span className="text-[#999]">*</span></label>
      <input type="text" name="name" value={formData.name} onChange={handleChange} required className={inputClass} placeholder="Full name" data-testid="commission-name" />
    </div>
    <div>
      <label className="text-[11px] text-[#bbb] block mb-2">Email <span className="text-[#999]">*</span></label>
      <input type="email" name="email" value={formData.email} onChange={handleChange} required className={inputClass} placeholder="your@email.com" data-testid="commission-email" />
    </div>
  </div>
);

const ProjectDetailsFields = ({ formData, handleChange }) => (
  <>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
      <div>
        <label className="text-[11px] text-[#bbb] block mb-2">Phone</label>
        <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className={inputClass} placeholder="Phone number" data-testid="commission-phone" />
      </div>
      <div>
        <label className="text-[11px] text-[#bbb] block mb-2">Subject of the commission <span className="text-[#999]">*</span></label>
        <input type="text" name="subject" value={formData.subject} onChange={handleChange} required className={inputClass} placeholder="e.g., Portrait, landscape, musician" data-testid="commission-subject" />
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
      <div>
        <label className="text-[11px] text-[#bbb] block mb-2">Preferred size</label>
        <select name="size" value={formData.size} onChange={handleChange} className={`${inputClass} appearance-none`} data-testid="commission-size">
          <option value="">Select a size</option>
          <option value="small">Small (up to 12 x 14 in)</option>
          <option value="medium">Medium (16 x 20 in)</option>
          <option value="large">Large (24 x 36 in)</option>
          <option value="xl">Extra large (36 x 48+ in)</option>
          <option value="unsure">Not sure yet</option>
        </select>
      </div>
      <div>
        <label className="text-[11px] text-[#bbb] block mb-2">Preferred medium</label>
        <select name="medium" value={formData.medium} onChange={handleChange} className={`${inputClass} appearance-none`} data-testid="commission-medium">
          <option value="">Select a medium</option>
          <option value="oil">Oil</option>
          <option value="acrylic">Acrylic</option>
          <option value="pastel">Pastel</option>
          <option value="pencil">Pencil / Charcoal</option>
          <option value="mixed">Mixed media</option>
          <option value="open">Open to suggestions</option>
        </select>
      </div>
      <div>
        <label className="text-[11px] text-[#bbb] block mb-2">Budget range</label>
        <select name="budget" value={formData.budget} onChange={handleChange} className={`${inputClass} appearance-none`} data-testid="commission-budget">
          <option value="">Select a range</option>
          <option value="300-500">$300 – $500</option>
          <option value="500-1000">$500 – $1,000</option>
          <option value="1000-2000">$1,000 – $2,000</option>
          <option value="2000+">$2,000+</option>
          <option value="discuss">Prefer to discuss</option>
        </select>
      </div>
    </div>
  </>
);

const CommissionForm = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', size: '', medium: '', budget: '', description: '' });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    const lines = [
      `Name: ${formData.name}`,
      `Email: ${formData.email}`,
      `Phone: ${formData.phone || '—'}`,
      `Subject: ${formData.subject}`,
      `Preferred size: ${formData.size || '—'}`,
      `Preferred medium: ${formData.medium || '—'}`,
      `Budget: ${formData.budget || '—'}`,
      '',
      'Vision:',
      formData.description,
    ];
    const subject = `Commission inquiry: ${formData.subject || 'New request'}`;
    const mailto = `mailto:${artistInfo.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join('\n'))}`;
    window.location.href = mailto;
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="commission-success">
        <CheckCircle size={36} className="text-green-500/70 mb-5" />
        <h3 className="text-lg font-light text-[#1a1a1a]">Your email is ready to send</h3>
        <p className="text-[13px] text-[#999] mt-3 max-w-sm">Your email app should have opened with your commission details. Just press send, and Sebastian will be in touch within 2-3 business days. If nothing opened, email <a href={`mailto:${artistInfo.email}`} className="border-b border-[#ddd] hover:text-[#1a1a1a]">{artistInfo.email}</a> directly.</p>
        <button onClick={() => setIsSubmitted(false)} className="mt-6 text-[12px] text-[#999] hover:text-[#1a1a1a] transition-colors border-b border-[#ddd]">
          Edit the form again
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-3xl space-y-7" data-testid="commission-form">
      <PersonalInfoFields formData={formData} handleChange={handleChange} />
      <ProjectDetailsFields formData={formData} handleChange={handleChange} />
      <div>
        <label className="text-[11px] text-[#bbb] block mb-2">Describe your vision <span className="text-[#999]">*</span></label>
        <textarea name="description" value={formData.description} onChange={handleChange} required rows={5} className={`${inputClass} resize-none`} placeholder="Tell me about the artwork you'd like created — the subject, any reference images you have, where it will hang, and anything else that's important to you." data-testid="commission-description" />
      </div>
      {error && <p className="text-[12px] text-red-500" data-testid="commission-error">{error}</p>}
      <div className="pt-2">
        <button type="submit" data-testid="commission-submit" className="inline-flex items-center gap-2.5 text-[12px] bg-[#1a1a1a] hover:bg-[#333] text-white px-8 py-3.5 transition-colors duration-300 disabled:opacity-50">
          <Send size={13} /> Submit commission inquiry
        </button>
      </div>
    </form>
  );
};

const CommissionRequest = () => {
  const process = commissionProcess;

  return (
    <div data-testid="commission-page">
      <section className="relative h-[45vh] md:h-[55vh] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${process.pastWork[0]})` }}>
          <div className="absolute inset-0 bg-black/40" />
        </div>
        <div className="relative z-10 h-full flex items-end">
          <div className="max-w-[1400px] mx-auto w-full px-6 md:px-12 pb-10">
            <h1 className="text-2xl md:text-4xl font-light text-white tracking-[0.01em]">Commission a work</h1>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
        <FadeIn><div className="max-w-2xl"><p className="text-[15px] md:text-[16px] leading-[1.85] text-[#555] font-light">{process.intro}</p></div></FadeIn>
      </section>

      <ProcessSteps steps={process.steps} />
      <PricingTimeline pricing={process.pricing} timeline={process.timeline} />
      <PastCommissionsGallery pastWork={process.pastWork} />

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-20">
        <FadeIn>
          <div className="border-t border-[#eee] pt-16">
            <h2 className="text-lg font-light text-[#1a1a1a] mb-3">Start your commission</h2>
            <p className="text-[13px] text-[#999] mb-10 max-w-lg">Fill out the form below with as much detail as possible. Sebastian will respond within 2-3 business days to discuss your project.</p>
          </div>
        </FadeIn>
        <FadeIn delay={100}><CommissionForm /></FadeIn>
      </section>
    </div>
  );
};

export default CommissionRequest;
